import { create } from 'zustand';
import type { RegimeData } from '../api/types';

/**
 * Live market-regime status per strategy (keyed by strategyId), fed by the
 * `regime` WS channel: a `regime-snapshot` on subscribe (last-known per gated
 * strategy) and a `regime` push per frequent regime-check tick (~5min).
 *
 * Independent of any portfolio, so the OPEN/CLOSED regime pill renders on
 * flat-state surfaces (strategy cards, live-wallet header) even when a basket
 * holds zero positions. This is the data the pill reads to ALWAYS show an
 * explicit open/closed status with its proximity-to-flip margin.
 *
 * Last-write-wins per strategy; resets on reload (it's a live convenience view,
 * not a durable record — the gateway re-snapshots on reconnect).
 */

type State = {
  byStrategy: Record<string, RegimeData>;
  /** Replace from a snapshot (one entry per gated strategy). */
  setSnapshot: (regimes: RegimeData[]) => void;
  /** Upsert a single strategy's regime status. */
  set: (regime: RegimeData) => void;
};

export const useRegimeStore = create<State>((set) => ({
  byStrategy: {},

  setSnapshot: (regimes) =>
    set((state) => {
      if (regimes.length === 0) return state;
      const next = { ...state.byStrategy };
      for (const r of regimes) next[r.strategyId] = r;
      return { byStrategy: next };
    }),

  set: (regime) =>
    set((state) => ({
      byStrategy: { ...state.byStrategy, [regime.strategyId]: regime },
    })),
}));

/** Stable selector: the regime status for one strategy (or undefined). */
export function selectRegime(
  strategyId: string | null | undefined,
): (s: State) => RegimeData | undefined {
  return (s) => (strategyId ? s.byStrategy[strategyId] : undefined);
}
